import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown } from 'lucide-react'
import { FactoringProvider, fetchFactoringStatus, removeCapitalDepotCredentials } from 'services/factoring'
import { cn } from 'utils/cn'
import { POPOVER_Z_INDEX } from '@/components/ds2/z-index'
import type { FactoringChipProvider } from './factoringChipProviders'

interface FactoringProviderMenuProps {
    currentProvider: FactoringChipProvider
    enabled: boolean
    providers: FactoringChipProvider[]
    selectedProvider: FactoringProvider | null
    onSelect: (provider: FactoringProvider) => void
    /** Omitted by a chip that has nothing to refresh — a deferred chip holds no lookup keys, so a
     *  refresh would resolve to the hook's "nothing to look up" return. */
    onRefresh?: () => Promise<void>
}

export const FactoringProviderMenu = ({ currentProvider, enabled, providers, selectedProvider, onSelect, onRefresh }: FactoringProviderMenuProps) => {
    const [open, setOpen] = useState(false)
    const [capitalDepotConnected, setCapitalDepotConnected] = useState(false)
    const [disconnectingCapitalDepot, setDisconnectingCapitalDepot] = useState(false)
    const [disconnectError, setDisconnectError] = useState<string | null>(null)
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false)
        }
        const handleScroll = (event: Event) => {
            if (menuRef.current && menuRef.current.contains(event.target as Node)) return
            setOpen(false)
        }
        document.addEventListener('mousedown', handleClickOutside)
        window.addEventListener('scroll', handleScroll, true)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            window.removeEventListener('scroll', handleScroll, true)
        }
    }, [open])

    useEffect(() => {
        if (!open) return
        let cancelled = false
        fetchFactoringStatus()
            .then((status) => {
                if (!cancelled) setCapitalDepotConnected(status?.capital_depot?.connected === true)
            })
            .catch(() => {
                if (!cancelled) setCapitalDepotConnected(false)
            })
        return () => {
            cancelled = true
        }
    }, [open])

    const disconnectCapitalDepot = async () => {
        setDisconnectingCapitalDepot(true)
        setDisconnectError(null)
        const result = await removeCapitalDepotCredentials()
        if (result.success) {
            setCapitalDepotConnected(false)
            setOpen(false)
            await onRefresh?.()
        } else {
            setDisconnectError(result.error || 'Failed to disconnect Capital Depot')
        }
        setDisconnectingCapitalDepot(false)
    }

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                disabled={!enabled}
                onClick={() => {
                    if (!enabled) return
                    if (!open && buttonRef.current) {
                        const rect = buttonRef.current.getBoundingClientRect()
                        setPosition({ top: rect.bottom + 6, left: rect.left })
                    }
                    setOpen((current) => !current)
                }}
                className={cn(
                    'flex items-center gap-1.5 px-3 bg-transparent border-0 border-r border-solid border-[var(--lh-border)] text-[var(--lh-fg)] font-medium transition-colors',
                    enabled ? 'cursor-pointer hover:bg-[var(--lh-surface-secondary)]' : 'cursor-default'
                )}
            >
                {currentProvider.shortLabel}
                {enabled && <ChevronDown aria-hidden className={cn('h-3.5 w-3.5 text-[var(--lh-fg-muted)] transition-transform', open && 'rotate-180')} />}
            </button>

            {open &&
                position &&
                createPortal(
                    <div
                        ref={menuRef}
                        role="menu"
                        className="fixed max-h-60 w-56 overflow-y-auto rounded-xl border border-[var(--lh-border)] bg-[var(--lh-surface)] py-1 shadow-overlay"
                        style={{ top: position.top, left: position.left, zIndex: POPOVER_Z_INDEX }}
                    >
                        <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-[var(--lh-fg-muted)]">Select Provider</div>
                        {providers.map((item) => {
                            const isSelected = item.value === selectedProvider
                            return (
                                <button
                                    key={item.value}
                                    type="button"
                                    role="menuitemradio"
                                    aria-checked={isSelected}
                                    onClick={() => {
                                        onSelect(item.value)
                                        setOpen(false)
                                    }}
                                    className={cn(
                                        'flex w-full cursor-pointer items-center justify-between border-none bg-transparent px-3 py-2 text-[12px]',
                                        isSelected
                                            ? 'bg-[var(--lh-accent-soft)] text-[var(--lh-accent)]'
                                            : 'text-[var(--lh-fg)] hover:bg-[var(--lh-surface-secondary)]'
                                    )}
                                >
                                    <span className={isSelected ? 'font-medium' : ''}>{item.label}</span>
                                    {isSelected && <Check aria-hidden className="h-4 w-4 text-[var(--lh-accent)]" />}
                                </button>
                            )
                        })}
                        {selectedProvider === FactoringProvider.CAPITAL_DEPOT && capitalDepotConnected && (
                            <button
                                type="button"
                                disabled={disconnectingCapitalDepot}
                                onClick={() => void disconnectCapitalDepot()}
                                className="w-full border-0 border-t border-solid border-[var(--lh-border)] bg-transparent px-3 py-2 text-left text-[12px] font-medium text-danger-soft-foreground hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {disconnectingCapitalDepot ? 'Disconnecting…' : 'Disconnect Capital Depot'}
                            </button>
                        )}
                        {disconnectError && <div className="px-3 py-2 text-[11px] text-danger-soft-foreground">{disconnectError}</div>}
                    </div>,
                    document.body
                )}
        </>
    )
}
